import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import Tag from 'antd/es/tag';
import DatePicker from 'antd/es/date-picker';
import Divider from 'antd/es/divider';
import Tooltip from 'antd/es/tooltip';

import { datePickerFormats } from 'config/common';

import useDatePicker from 'hooks/useDatePicker';
import useStats from 'hooks/useStats';

import { StatType } from 'services/stats';

import MainLayout from 'layouts/Main.layout';
import { Row, Col, Space } from 'components/layout';
import { Text, Title } from 'components/Text';
import PageHeader from 'components/MainPageHeader';
import RadioGroup from 'components/RadioGroup';
import Icon from 'components/Icon';

import CartFlowCard from '../components/CartFlowCard';
import CartStatCard from '../components/CartStatCard';
import CartCountStat from '../components/CartCountStat';
import FlowRatioChart from '../components/FlowRatioChart';
import CartCountChart from '../components/CartCountChart';

import { formatDuration, toTimezone } from 'helpers/date';

const CartsFlowPageHeader = styled(PageHeader)`
  & .ant-page-header-content {
    padding-top: unset;
  }
`;

const CartsFlowPage = () => {
  const [shift, setShift] = useState(1);
  const datePicker = useDatePicker('daily', {
    queryParam: 'dt',
    initalValue: new Date(),
  });

  const statsParams = useMemo(() => {
    const date = datePicker.date;

    return {
      shiftDate: date ? date.format('YYYY-MM-DD') : date,
      shiftNo: shift,
    };
  }, [datePicker.date, shift]);

  const { state: { isLoading, stats } } = useStats(StatType.cartStatsShift, statsParams, {
    autoFetch: true,
    reset: true,
  });

  const currentStats = useMemo(() => {
    if (stats.current?.length) {
      const current = stats.current[0];
      const toPercent = (val) => {
        return Math.round((val * 100) / current.tracked_carts);
      };

      return {
        available: current.cur_empty,
        percentAvailable: toPercent(current.cur_empty),
        inbound: current.cur_in_container,
        percentInbound: toPercent(current.cur_in_container),
        outbound: current.cur_in_trailer,
        percentOutbound: toPercent(current.cur_in_trailer),
        transit: current.cur_in_area,
        percentTransit: toPercent(current.cur_in_area),
        totalTracked: current.tracked_carts,
        info: current.notes,
      };
    }
    return {};
  }, [stats]);

  const lastHourStats = useMemo(() => {
    if (stats.last_hour?.length) {
      const lastHour = stats.last_hour[0];

      return {
        avgCompleted: Math.round(lastHour.avg_completed_carts_perhour_perperson * 10) / 10,
        totalPeopleInbound: lastHour.tot_people_inbound,
        totalCartsInbound: lastHour.tot_carts_loaded,
        totalPeopleOutbound: lastHour.tot_people_outbound,
        totalCarsOutbound: lastHour.tot_carts_unloaded,
        cartsDiffChange: lastHour.tot_carts_loaded - lastHour.tot_carts_unloaded,

        info: lastHour.notes,
      };
    }
    return {};
  }, [stats]);

  const shiftStats = useMemo(() => {
    if (stats.shift_stats?.length) {
      const shift = stats.shift_stats[0];

      return {
        // left stats
        avgTrialerLoadTime: shift.avg_trailer_load_duration_hrs * 3600_000,
        totalTrialersLoaded: shift.tot_trailer_loads,
        avgCompletedCarts: Math.round(shift.avg_completed_carts_perhour_perperson * 10) / 10,
        cartUtilizationByVolume: Math.round(shift.avg_cart_utilization_by_volume * 100),

        // top stats
        avgCartLoadTime: shift.avg_container_mins * 60_000,
        avgCartTransitTime: shift.avg_area_mins * 60_000,
        avgCartUnloadTime: shift.avg_trailer_mins * 60_000,

        // count charts
        totalCartsLoaded: shift.tot_carts_unloaded,
        totalCartsUnloaded: shift.tot_carts_loaded,
        totalCarts: Math.ceil(Math.max(shift.tot_carts_unloaded, shift.tot_carts_loaded) * 1.3),

        info: shift.notes,
      };
    }
    return {};
  }, [stats]);

  const flowStats = useMemo(() => {
    if (stats.flow_ratio) {
      const flowRatio = stats.flow_ratio;
      const data = flowRatio.stats.map(row => {
        return {
          time: toTimezone(new Date(row.ts * 1000), flowRatio.tz_offset).toISOString(),
          ratio: Math.round(row.flow_ratio_cart * 100) / 100,
        };
      });
      const current = data[data.length - 1];

      return {
        data,
        current,
        info: flowRatio.notes,
      };
    }
    return {};
  }, [stats]);

  return (
    <MainLayout>
      <CartsFlowPageHeader
        title="Carts"
        icon="shopping-cart"
      />
      <Row style={{ padding: '12px 24px' }}>
        <Space size="large">
          <DatePicker
            mode={datePicker.mode}
            picker={datePicker.mode}
            value={datePicker.date}
            format={datePickerFormats.daily}
            onChange={date => datePicker.setDate(date)}
            style={{ width: '220px' }}
            disabledDate={(current) => {
              return moment(current).toDate() > moment().toDate();
            }}
            allowClear={false}
          />
          <RadioGroup
            options={[
              {
                label: 'First Shift',
                value: 1,
              },
              {
                label: 'Second Shift',
                value: 2,
              },
            ]}
            onChange={(e) => {
              setShift(e.target.value);
            }}
            value={shift}
          />
        </Space>
      </Row>

      {/* current stats */}
      <Row style={{ padding: '12px 24px 6px 24px' }}>
        <Col span={24}>
          <CartFlowCard
            title="Current Cart Status"
            info={currentStats?.info}
            titleExtra={typeof currentStats.totalTracked === 'number' ? (
              <Text style={{ fontSize: '16px', color: '#000000' }}>
                {currentStats.totalTracked} Total Carts
              </Text>
            ) : null}
          >
            <Row gutter={[24, 12]} align="stretch" style={{ flexWrap: 'nowrap', justifyContent: 'space-evenly', paddingTop: '12px' }}>
              <Col span={6}>
                <CartCountStat
                  name="Available"
                  count={currentStats?.available || 0}
                  percentage={currentStats?.percentAvailable || 0}
                  color="#979899"
                />
              </Col>
              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>
              <Col span={5}>
                <CartCountStat
                  name="Inbound"
                  icon={<Icon name="arrow-down" color="#42B0D5" size={24} />}
                  count={currentStats?.inbound || 0}
                  percentage={currentStats?.percentInbound || 0}
                  color="#42B0D5"
                />
              </Col>
              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>
              <Col span={5}>
                <CartCountStat
                  name="In Transit"
                  icon={<Icon name="arrow-up-right" color="#FAAD14" size={24} />}
                  count={currentStats?.transit || 0}
                  percentage={currentStats?.percentTransit || 0}
                  color="#FAAD14"
                />
              </Col>
              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>
              <Col span={5}>
                <CartCountStat
                  name="Outbound"
                  icon={<Icon name="arrow-up" color="#2F54EB" size={24} />}
                  count={currentStats?.outbound || 0}
                  percentage={currentStats?.percentOutbound || 0}
                  color="#2F54EB"
                />
              </Col>
            </Row>
          </CartFlowCard>
        </Col>
      </Row>

      {/* last hour stats */}
      <Row style={{ padding: '6px 24px' }}>
        <Col span={24}>
          <CartFlowCard
            title="Last Hour"
            info={lastHourStats?.info}
          >
            <Row gutter={[24, 12]} align="stretch" style={{ flexWrap: 'nowrap', justifyContent: 'space-evenly', paddingTop: '12px' }}>
              {/* Average completed carts */}
              <Col span={6}>
                <CartStatCard
                  stat={{
                    title: 'Average completed carts/hour/person',
                    value: lastHourStats?.avgCompleted || 0,
                    titleAlign: ['center', 'bottom']
                  }}
                  style={{
                    maxWidth: '20vw',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Col>

              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>

              {/* Carts loaded */}
              <Col span={5}>
                <CartStatCard
                  stat={[
                    {
                      title: 'Carts loaded',
                      value: lastHourStats?.totalCartsInbound || 0,
                      titleAlign: ['center', 'right']
                    },
                    {
                      title: 'Inviduals',
                      value: lastHourStats?.totalPeopleInbound || 0,
                      titleAlign: ['center', 'right']
                    },
                  ]}
                  style={{
                    backgroundColor: "#C7F4FC",
                    maxWidth: '15vw',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Col>

              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>

              {/* Carts in transit */}
              <Col span={5}>
                <CartStatCard
                  stat={{
                    title: `${(lastHourStats?.cartsDiffChange || 0) >= 0 ? 'Increase' : 'Descrease'} carts in transit`,
                    value: `${(lastHourStats?.cartsDiffChange || 0) >= 0 ? '+' : ''}${lastHourStats?.cartsDiffChange || 0}`,
                    titleAlign: ['center', 'bottom'],
                  }}
                  style={{
                    backgroundColor: "#FFF1B8",
                    maxWidth: '15vw',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Col>

              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>

              {/* Carts unloaded */}
              <Col span={5}>
                <CartStatCard
                  stat={[
                    {
                      title: 'Carts unloaded',
                      value: lastHourStats?.totalCarsOutbound || 0,
                      titleAlign: ['center', 'right']
                    },
                    {
                      title: 'Inviduals',
                      value: lastHourStats?.totalPeopleOutbound || 0,
                      titleAlign: ['center', 'right']
                    },
                  ]}
                  style={{
                    backgroundColor: "#D6E4FF",
                    maxWidth: '15vw',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Col>
            </Row>
          </CartFlowCard>
        </Col>
      </Row>

      {/* shift stats */}
      <Row style={{ padding: '6px 24px 24px 24px' }}>
        <Col span={24}>
          <CartFlowCard
            title="Shift Statistics"
            info={shiftStats?.info}
          >
            <Row gutter={[24, 12]} align="stretch" style={{ flexWrap: 'nowrap', justifyContent: 'space-evenly', paddingTop: '12px' }}>
              <Col span={6}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <CartStatCard
                      stat={{
                        title: 'Average trailer load time',
                        value: formatDuration(shiftStats?.avgTrialerLoadTime || 0, false),
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        maxWidth: '20vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <CartStatCard
                      stat={{
                        title: 'Total trailers loaded',
                        value: shiftStats?.totalTrialersLoaded || 0,
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        maxWidth: '20vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <CartStatCard
                      stat={{
                        title: 'Average completed carts/hour/person',
                        value: shiftStats?.avgCompletedCarts || 0,
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        maxWidth: '20vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <CartStatCard
                      stat={{
                        title: 'Average cart utilization by volume',
                        value: shiftStats?.cartUtilizationByVolume || 0,
                        suffix: '%',
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        maxWidth: '20vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                </Row>
              </Col>

              <Col>
                <Divider type="vertical" style={{ height: '100%' }} />
              </Col>

              <Col span={17}>
                <Row align="stretch" style={{ flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                  <Col span={6}>
                    <CartStatCard
                      stat={{
                        title: 'Average cart time in container',
                        value: formatDuration(shiftStats?.avgCartLoadTime || 0, false),
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        backgroundColor: "#C7F4FC",
                        maxWidth: '15vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                  <Col>
                    <Divider type="vertical" style={{ height: '100%' }} />
                  </Col>
                  <Col span={6}>
                    <CartStatCard
                      stat={{
                        title: 'Average cart dwell time',
                        value: formatDuration(shiftStats?.avgCartTransitTime || 0, false),
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        backgroundColor: "#FFF1B8",
                        maxWidth: '15vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                  <Col>
                    <Divider type="vertical" style={{ height: '100%' }} />
                  </Col>
                  <Col span={6}>
                    <CartStatCard
                      stat={{
                        title: 'Average cart time in trailer',
                        value: formatDuration(shiftStats?.avgCartUnloadTime || 0, false),
                        titleAlign: ['center', 'bottom']
                      }}
                      style={{
                        backgroundColor: "#D6E4FF",
                        maxWidth: '15vw',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Col>
                </Row>

                {/* Flow chart */}
                <Row style={{ padding: '24px' }}>
                  <Col span={24}>
                    <Row justify="space-between" style={{ width: '100%' }}>
                      <Col>
                        <Space style={{ alignItems: 'center' }}>
                          <Title level={4} style={{ margin: 0 }}>Flow Ratio</Title>
                          <Tooltip title={flowStats?.info}>
                            <Icon
                              name="info"
                              size={16}
                              color="#B9C2C9"
                              style={{
                                marginTop: '4px',
                              }}
                            />
                          </Tooltip>
                        </Space>
                      </Col>
                      <Col>
                        <Tag>
                          <Text>
                            Current: {flowStats?.current?.ratio || 0}
                          </Text>
                        </Tag>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <FlowRatioChart
                      isLoading={isLoading}
                      data={flowStats?.data || []}
                    />
                  </Col>
                </Row>

                {/* Count charts */}
                <Row gutter={[24, 24]} style={{ padding: '24px' }}>
                  <Col span={24}>
                    <CartCountChart
                      label="Total carts loaded"
                      count={shiftStats?.totalCartsLoaded || 0}
                      total={shiftStats?.totalCarts}
                      color="#42B0D5"
                    />
                  </Col>
                  <Col span={24}>
                    <CartCountChart
                      label="Total carts unloaded"
                      count={shiftStats?.totalCartsUnloaded || 0}
                      total={shiftStats?.totalCarts}
                      color="#2F54EB"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </CartFlowCard>
        </Col>
      </Row>

    </MainLayout>
  );
}

export default CartsFlowPage;